import React, { useState, useEffect, useMemo, useRef } from "react";
import { Layout, Menu, ConfigProvider, Spin, Modal, message, Tooltip, Select } from "antd";
import { LogoutOutlined, CloseOutlined, BankOutlined } from "@ant-design/icons";
import logo from "../../assets/logo.jpg";
import Tlogo from "../../assets/Tlogo.jpg";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ChatApp from "../ChatApp";
import { logoutRequest } from "../../redux/actions/authActions";
import { notification } from "antd";
import { useSelector } from "react-redux";
import { MENU_CONFIG } from "../../utils/menu/menuConfig";
import { openMenuTab, getTabIdentity, sanitizeTabName } from "../../utils/menu/menutabs";
import { socket } from "../../utils/socket";
import useSocket from "../../utils/hooks/useSocket";
import { getBranchesRequest } from "../../redux/actions/bookingActions";
import {
  useBranchId,
  useBranchSync,
  useApplyBranch,
} from "../../utils/hooks/useBranch";
import {
  DEFAULT_BRANCH_ID,
  hasDirtyForm,
  setBranchId,
} from "../../utils/branch";
import { clearTabState } from "../../utils/session";
import { uuid } from "../../utils/uuid";
import {
  COPY_ROLE_ID,
  GUARD_CLASS,
  NAMES_CLASS,
  isNameCellSelection,
} from "../../utils/copyGuard";
import {
  getChatConversationsRequest,
  receiveSocketMessage,
  clearChatMessages,
} from "../../redux/actions/chatAction";

const { Sider, Content } = Layout;
const SIDEBAR_EXPANDED = 200;
const SIDEBAR_COLLAPSED = 72;

type AdminLayoutProps = { children: React.ReactNode };

const BookingLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { menus, submenus, roleid, user, token } = useSelector((state: any) => state.auth);
  const employeename = user?.username || localStorage.getItem("username") || "";
  const allowedIds = useMemo(() => [...(menus ?? []), ...(submenus ?? [])], [menus, submenus]);
  const [tabId] = useState(() => uuid());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const LOCK_STALE_MS = 15_000;
  const HEARTBEAT_INTERVAL_MS = 5_000;

  const branchId = useBranchId();
  const applyBranch = useApplyBranch(tabId);
  useBranchSync(tabId);

  const branches = useSelector((state: any) => state.booking?.branches ?? []);
  const branchesLoading = useSelector((state: any) =>
    Boolean(state.booking?.branchesLoading)
  );
  const branchesError = useSelector(
    (state: any) => state.booking?.branchesError ?? null
  );

  const branchOptions = useMemo(() => {
    const list = (branches as any[]).map((b) => ({
      value: b.id,
      label: b.name,
    }));
    return list.some((o) => o.value === branchId)
      ? list
      : [{ value: branchId, label: branchId }, ...list];
  }, [branches, branchId]);

  const activeBranchLabel =
    branchOptions.find((o) => o.value === branchId)?.label ?? branchId;

  const handleBranchChange = (next: string) => {
    if (next === branchId) return;

    if (hasDirtyForm()) {
      Modal.confirm({
        title: "Discard unsaved booking?",
        content:
          "A booking form with unsaved details is open. Switching branch will discard it.",
        okText: "Switch branch",
        okButtonProps: { danger: true },
        cancelText: "Stay here",
        onOk: () => applyBranch(next),
      });
      return;
    }

    applyBranch(next);
  };

  const branchCorrected = useRef(false);
  useEffect(() => {
    if (branchCorrected.current || branchesLoading) return;
    if (!(branches as any[]).length) return;
    if ((branches as any[]).some((b) => b.id === branchId)) return;

    branchCorrected.current = true;
    const fallback = (branches as any[]).some((b) => b.id === DEFAULT_BRANCH_ID)
      ? DEFAULT_BRANCH_ID
      : (branches as any[])[0].id;
    message.warning(
      `Branch ${branchId} is no longer available. Switched to ${fallback}.`
    );
    setBranchId(fallback, tabId);
  }, [branches, branchesLoading, branchId, tabId]);

  useEffect(() => {
    if (!token) {
      navigate("/", { replace: true });
    }
  }, [token, navigate]);

  const getSelectedKey = (): string => {
    const paths = MENU_CONFIG
      .filter((m) => !m.parentId)
      .map((m) => m.path);
    const match = paths
      .filter((p) => pathname.startsWith(p))
      .sort((a, b) => b.length - a.length)[0];
    return match ?? "";
  };

  const navItems = MENU_CONFIG
    .filter((m) => allowedIds.includes(m.id) && !m.parentId)
    .map((m) => ({
      key: m.path,
      icon: m.icon,
      label: m.label,
      onClick: () => openMenuTab(m.path === "/booking" ? "/booking/live" : m.path),
    }));

  useEffect(() => {
    const handleCompanyCreated = (data: any) => {
      notification.success({
        message: "New Company Registered",
        description: `${data.CompanyName} has been added.`,
        placement: "topRight",
      });
    };

    socket.on("company_created", handleCompanyCreated);

    return () => {
      socket.off("company_created", handleCompanyCreated);
    };
  }, []);

  const canCopyNames = Number(roleid) === COPY_ROLE_ID;

  useEffect(() => {
    const guardClipboard = (e: ClipboardEvent) => {
      if (e.type === "copy" && canCopyNames && isNameCellSelection()) return;
      e.preventDefault();
    };

    document.body.classList.add(GUARD_CLASS);
    if (canCopyNames) document.body.classList.add(NAMES_CLASS);

    document.addEventListener("copy", guardClipboard);
    document.addEventListener("cut", guardClipboard);

    return () => {
      document.body.classList.remove(GUARD_CLASS, NAMES_CLASS);
      document.removeEventListener("copy", guardClipboard);
      document.removeEventListener("cut", guardClipboard);
    };
  }, [canCopyNames]);

  useEffect(() => {
    if (!pathname || allowedIds.length === 0) return;

    const matchedMenu = MENU_CONFIG
      .filter((m) => pathname.startsWith(m.path))
      .sort((a, b) => b.path.length - a.path.length)[0];

    const isDenied = !matchedMenu || !allowedIds.includes(matchedMenu.id);

    if (isDenied) {
      navigate("/", { replace: true });
    }
  }, [pathname, allowedIds, navigate]);

  const activeTabKey = useMemo(() => getTabIdentity(pathname), [pathname]);

  useEffect(() => {
    if (!activeTabKey) return;
    window.name = sanitizeTabName(activeTabKey);
  }, [activeTabKey]);

  useEffect(() => {
    if (!activeTabKey) return;
    if (pathname === "/") return;

    const lockKey = `booking_tab_lock:${activeTabKey}`;
    const heartbeatKey = `booking_tab_heartbeat:${activeTabKey}`;
    const existingLock = localStorage.getItem(lockKey);

    if (existingLock && existingLock !== tabId) {
      const lastHeartbeat = Number(localStorage.getItem(heartbeatKey) || "0");
      const isStale = Date.now() - lastHeartbeat > LOCK_STALE_MS;

      if (!isStale) {
        let countdown = 3;
        const modal = Modal.warning({
          title: "Duplicate Tab Detected",
          content: `This page is already open in another tab. Redirecting in ${countdown}s...`,
          okText: "Go Back Now",
          onOk: () => {
            clearInterval(interval);
            navigate("/", { replace: true });
          },
        });
        const interval = setInterval(() => {
          countdown--;
          if (countdown <= 0) {
            clearInterval(interval);
            modal.destroy();
            navigate("/", { replace: true });
          } else {
            modal.update({
              content: `This page is already open in another tab. Redirecting in ${countdown}s...`,
            });
          }
        }, 1000);

        return () => {
          clearInterval(interval);
          modal.destroy();
        };
      }
    }

    localStorage.setItem(lockKey, tabId);
    localStorage.setItem(heartbeatKey, String(Date.now()));

    const heartbeatTimer = setInterval(() => {
      if (localStorage.getItem(lockKey) === tabId) {
        localStorage.setItem(heartbeatKey, String(Date.now()));
      }
    }, HEARTBEAT_INTERVAL_MS);

    const releaseIfOwner = () => {
      if (localStorage.getItem(lockKey) === tabId) {
        localStorage.removeItem(lockKey);
        localStorage.removeItem(heartbeatKey);
      }
    };

    window.addEventListener("beforeunload", releaseIfOwner);
    window.addEventListener("pagehide", releaseIfOwner);

    return () => {
      clearInterval(heartbeatTimer);
      window.removeEventListener("beforeunload", releaseIfOwner);
      window.removeEventListener("pagehide", releaseIfOwner);
      releaseIfOwner();
    };
  }, [activeTabKey, pathname, navigate, tabId]);

  const dispatch = useDispatch();

  const { getSocket, socket: chatSocket } = useSocket();
  const chatConversations = useSelector((state: any) => state.chat?.conversations || []);

  const bootstrapFetched = useRef(false);
  useEffect(() => {
    if (!token || bootstrapFetched.current) return;
    bootstrapFetched.current = true;
    dispatch(getBranchesRequest());
    dispatch(getChatConversationsRequest());
  }, [dispatch, token]);
  const unread = useMemo(
    () => chatConversations.reduce((sum: number, c: any) => sum + (c?.unreadCount || 0), 0),
    [chatConversations]
  );

  useEffect(() => {
    const s = getSocket();
    if (!s || chatOpen) return;
    const onReceive = (data: any) => {
      if (data?.message && data?.conversationId) {
        dispatch(receiveSocketMessage(data.message, data.conversationId));
      }
    };
    s.on("message:receive", onReceive);
    return () => { s.off("message:receive", onReceive); };
  }, [getSocket, chatSocket, chatOpen, dispatch]);

  const handleLogout = () => {
    Modal.confirm({
      title: "Confirm Logout",
      content: "Are you sure you want to logout?",
      okText: "Yes, Logout",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: () => {
        try {
          const logoutChannel = new BroadcastChannel("tt_logout_channel");
          logoutChannel.postMessage({ type: "LOGOUT", senderId: tabId, timestamp: Date.now() });
          setTimeout(() => {
            try { logoutChannel.close(); } catch (_) { }
          }, 1000);
        } catch (_) { }

        try {
          localStorage.setItem("tt_logout_signal", String(Date.now()));
        } catch (_) { }

        // Tab state only - logoutSaga still needs to read the token back out of
        // storage to build its server-side logout payload. clearSession() runs
        // there, once the API call has been made.
        clearTabState();

        dispatch(logoutRequest());
        message.success("Logged out successfully!");
        navigate("/", { replace: true });
      },
    });
  };

  useEffect(() => {
    const handleRemoteLogout = () => {
      dispatch(logoutRequest());
      try {
        window.close();
      } catch (_) { }
      setTimeout(() => {
        if (!document.hidden) {
          navigate("/", { replace: true });
        }
      }, 100);
    };

    let logoutChannel: BroadcastChannel | null = null;
    try {
      logoutChannel = new BroadcastChannel("tt_logout_channel");
      logoutChannel.onmessage = (event) => {
        const { type, senderId } = event.data || {};
        if (type !== "LOGOUT" || senderId === tabId) return;
        handleRemoteLogout();
      };
    } catch (_) { }

    const handleStorageEvent = (event: StorageEvent) => {
      if (
        event.key === "tt_logout_signal" ||
        (event.key === "token" && !event.newValue)
      ) {
        handleRemoteLogout();
      }
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => {
      if (logoutChannel) {
        try { logoutChannel.close(); } catch (_) { }
      }
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [dispatch, navigate, tabId]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);

      notification.success({
        message: "Internet Connected",
        description: "You are back online.",
        placement: "topRight",
        duration: 3,
      });
    };

    const handleOffline = () => {
      setIsOnline(false);

      notification.error({
        message: "Internet Disconnected",
        description: "Please check your network connection.",
        placement: "topRight",
        duration: 5,
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const PRIMARY = "#884699f5";

  const handleOpenChat = () => {
    setChatOpen(true);
  };

  const handleCloseChat = () => {
    setChatOpen(false);
    dispatch(clearChatMessages());
  };

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: PRIMARY, borderRadius: 14 },
        components: {
          Table: {
            rowHoverBg: "#ffffff",
          },
          Menu: {
            itemBorderRadius: 12,
            itemHeight: 46,
            itemColor: "rgba(255,255,255,0.75)",
            itemHoverColor: "#fff",
            itemHoverBg: "rgba(255,255,255,0.08)",
            itemSelectedBg: "rgba(255,255,255,0.16)",
            itemSelectedColor: "#fff",
            itemActiveBg: "rgba(255,255,255,0.12)",
            iconSize: 18,
          },
          Select: {
            selectorBg: "transparent",
            colorTextPlaceholder: "rgba(255,255,255,0.55)",
            colorIcon: "rgba(255,255,255,0.65)",
            colorIconHover: "#fff",
          },
        },
      }}
    >
      <Layout
        style={{
          height: "100vh",
          overflow: "hidden",
          background: "#f5f5f5",
        }}
      >
        <Sider
          collapsed={collapsed}
          width={SIDEBAR_EXPANDED}
          collapsedWidth={SIDEBAR_COLLAPSED}
          trigger={null}
          breakpoint="xl"
          onBreakpoint={(broken) => setCollapsed(broken)}
          style={{
            background: PRIMARY,
            flexShrink: 0,
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "4px 0 24px rgba(0,0,0,0.12)",
          }}
        >
          {/* Brand header */}
          <div
            style={{
              height: 76,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              padding: collapsed ? 0 : "0 18px",
              color: "#fff",
              fontWeight: 800,
              fontSize: 20,
              borderBottom: "1px solid rgba(255,255,255,.12)",
              flexShrink: 0,
            }}
          >
            <img
              src={logo}
              alt="Logo"
              onClick={() => setCollapsed(!collapsed)}
              style={{
                width: 35,
                height: 34,
                borderRadius: 5,
                objectFit: "cover",
                boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
                flexShrink: 0,
                cursor: "pointer",
              }}
            />
            {/* {!collapsed && (
              <span
                style={{
                  whiteSpace: "nowrap",
                  fontSize: 22,
                  letterSpacing: "0.3px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <img
                  src={Tlogo}
                  alt="Tlogo"
                  style={{
                    height: 32,
                    objectFit: "contain",
                    marginTop: "10px"
                  }}
                />
              </span>
            )} */}
          </div>

          <div
            style={{
              padding: collapsed ? "12px 0" : "12px 18px",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: 10,
              overflow: "hidden",
              background: "rgba(255,255,255,0.04)",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                minWidth: 32,
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.18)",
                border: "1px solid rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {employeename?.charAt(0)?.toUpperCase() || "U"}
            </div>

            {!collapsed && (
              <div style={{ minWidth: 0, overflow: "hidden" }}>
                <div
                  style={{
                    whiteSpace: "nowrap",
                    fontSize: 14.5,
                    color: "rgba(255,255,255,0.92)",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {employeename}
                </div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: "rgba(255,255,255,0.55)",
                    marginTop: 1,
                  }}
                >
                  Online
                </div>
              </div>
            )}
          </div>

          {/* Nav */}
          <div
            style={{
              flex: 1,
              paddingTop: 14,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[getSelectedKey()]}
              items={navItems}
              style={{
                background: "transparent",
                border: "none",
                padding: collapsed ? "0 8px" : "0 12px",
              }}
            />
          </div>

          {/* Logout */}
          <div
            style={{
              padding: collapsed ? "10px 8px 16px" : "10px 12px 16px",
              borderTop: "1px solid rgba(255,255,255,0.1)",
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {collapsed ? (
              <Tooltip title="Logout" placement="right">
                <button
                  onClick={handleLogout}
                  aria-label="Logout"
                  style={{
                    width: "100%",
                    height: 42,
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.06)",
                    border: "none",
                    color: "rgba(255,255,255,0.85)",
                    fontSize: 17,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "background 0.15s, color 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#ef4444";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.85)";
                  }}
                >
                  <LogoutOutlined />
                </button>
              </Tooltip>
            ) : (
              <button
                onClick={handleLogout}
                style={{
                  width: "100%",
                  height: 42,
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.06)",
                  border: "none",
                  color: "rgba(255,255,255,0.85)",
                  fontSize: 14,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "0 14px",
                  cursor: "pointer",
                  transition: "background 0.15s, color 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#ef4444";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                  e.currentTarget.style.color = "rgba(255,255,255,0.85)";
                }}
              >
                <LogoutOutlined />
                Logout
              </button>
            )}
          </div>
        </Sider>

        <Layout
          style={{
            flex: 1,
            minWidth: 0,
            transition: "all 0.2s ease",
            overflow: "hidden",
            height: "100vh",
          }}
        >
          <Content
            className="tt-content-scroll"
            style={{
              margin: 0,
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(24px)",
              borderRadius: 28,
              border: "1px solid rgba(255,255,255,0.2)",
              height: "100%",
              overflowY: "auto",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {!isOnline && (
              <div
                style={{
                  background: "#ff0004ff",
                  color: "#fff",
                  textAlign: "center",
                  padding: "10px",
                  fontWeight: 600,
                }}
              >
                🔴 No Internet Connection
              </div>
            )}
            <Spin size="large" spinning={false}>
              {children}
            </Spin>
          </Content>
        </Layout>

        <>
          <style>{`
            /* The vertical scrollbar is hidden on purpose. Scrolling itself is
               untouched - wheel, trackpad, touch and keyboard all still work;
               the browser just does not paint the bar. Trade-off, and the
               reason this was once made visible again: a long page gives no
               visual hint that there is more content below, so it can look cut
               off. width:0 rather than display:none keeps the horizontal bar,
               which is the only cue that wide tables scroll sideways. */
            .tt-content-scroll {
              scrollbar-width: none;      /* Firefox - hides both axes, no per-axis control */
              -ms-overflow-style: none;   /* old Edge/IE */
            }
            .tt-content-scroll::-webkit-scrollbar {
              width: 0;                   /* Chrome, Edge, Safari - vertical only */
            }

            /* Spin wraps every page in two shrink-wrapping divs, which breaks
               the height:100% chain for pages that want to fit the viewport
               instead of growing the Content scroller. */
            .tt-content-scroll > .ant-spin,
            .tt-content-scroll > .ant-spin > .ant-spin-container {
              height: 100%;
            }

            @keyframes fabPulse {
              0%   { box-shadow: 0 0 0 0   rgba(136,70,153,0.55); }
              70%  { box-shadow: 0 0 0 12px rgba(136,70,153,0); }
              100% { box-shadow: 0 0 0 0   rgba(136,70,153,0); }
            }
            @keyframes chatSlideUp {
              from { opacity: 0; transform: translateY(20px) scale(0.97); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
            .tt-chat-fab {
              position: fixed;
              bottom: 28px;
              right: 28px;
              z-index: 999;
              width: 56px; height: 56px;
              border-radius: 50%;
              background: ${PRIMARY};
              border: none; cursor: pointer;
              display: flex; align-items: center; justify-content: center;
              box-shadow: 0 8px 28px rgba(136,70,153,0.45);
              transition: transform 0.2s, box-shadow 0.2s;
            }
            .tt-chat-fab:hover {
              transform: scale(1.08);
              box-shadow: 0 12px 36px rgba(136,70,153,0.55);
            }
            .tt-chat-fab:active { transform: scale(0.95); }
            .tt-chat-fab.has-unread { animation: fabPulse 2s infinite; }

            .tt-chat-overlay {
              position: fixed;
              bottom: 96px;
              right: 28px;
              z-index: 998;
              width: min(880px, calc(100vw - 56px));
              height: min(580px, calc(100vh - 120px));
              border-radius: 20px;
              box-shadow: 0 32px 80px rgba(78,47,114,0.22), 0 4px 20px rgba(0,0,0,0.08);
              border: 1px solid rgba(136,70,153,0.12);
              animation: chatSlideUp 0.28s cubic-bezier(0.16,1,0.3,1) both;
            }
          `}</style>

          {chatOpen && (
            <div className="tt-chat-overlay">
              <div style={{
                position: "absolute", top: 12, right: 12, zIndex: 10,
              }}>
                <button
                  onClick={handleCloseChat}
                  style={{
                    width: 30, height: 30, borderRadius: 9,
                    background: "rgba(136,70,153,0.12)",
                    border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: PRIMARY, transition: "background 0.15s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = "rgba(136,70,153,0.22)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "rgba(136,70,153,0.12)")}
                >
                  <CloseOutlined style={{ fontSize: 13 }} />
                </button>
              </div>
              <ChatApp />
            </div>
          )}
          <button
            className={`tt-chat-fab${unread > 0 && !chatOpen ? " has-unread" : ""}`}
            onClick={chatOpen ? handleCloseChat : handleOpenChat}
            aria-label="Open chat"
          >
            {chatOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            )}
            {unread > 0 && !chatOpen && (
              <span style={{
                position: "absolute", top: -2, right: -2,
                minWidth: 20, height: 20, borderRadius: 10,
                background: "#ef4444", color: "#fff",
                fontSize: 11, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "0 5px", border: "2.5px solid #fff",
              }}>{unread}</span>
            )}
          </button>
        </>

      </Layout>
    </ConfigProvider>
  );
};

export default BookingLayout;
